import React, { useState, useEffect } from 'react';
import { useInterval } from '../../../helpers/customHooks';

import Timeline from './Map/mapUtils/Timeline';
import PracticeMap from './Map/PracticeMapContainer';
// import LoadingDialog from './Map/mapUtils/LoadingDialog';
import ActionButtons from './Map/mapUtils/ActionButtons';

import { getRealTimeActualSeconds, removeDecimalsFromSecs, checkOnProgressivePeriodChange, getPracticeViewerStartTime, isNotCurrentlyLive, cleanControllerSet } from '../../../helpers/helpers';
import { 
  DEFAULT_LOADING_SECS, 
  DEFAULT_FILTERS_STATE,
  CONTROLLER_SET, 
  LIVE_PRACTICE, 
  LIVE_DEFAULT_LOADING_SECS, PAST_PRACTICE, METRICS_CATEGORIE, 
  PAST_GAME} from '../../../config/constants';
import { MapViewerProps, PlayersTrails } from './types';
import { Drill } from '../../../config/commonTypes';
import { useDataEventsDispatch, usePracticeViewerState, useTablePropertiesState } from '../../../store/hooks';
import { useParams } from 'react-router-dom';

let fetchTimeout : ReturnType<typeof setTimeout> | undefined;

const MapViewer = ({
  field, fields, onPlay, setField, setOnPlay, wasOnPlay, mapImage, setWasOnPlay, 
  dimensions, currentTab
} : MapViewerProps) => {

  const { 
    blinks, 
    reqSecs, 
    metricSec, 
    hasBlinks, 
    isFetching,
    practiceData,
    shouldMapMount,
    fetchTries = 0
  } = usePracticeViewerState();
  const { pinnedRows = [] } = useTablePropertiesState();
  const { id: practiceId } = useParams<{ id: string }>();
  const { 
    getPlayersMetrics,
    getPracticePlayback,
    livePracticePlayers,
    getSpecificFieldImage,
    clearPlayersTracksData,
    setMetricCurrentSecond
  } = useDataEventsDispatch();

  const [show, setOnShow] = useState(DEFAULT_FILTERS_STATE);
  // const [showModal, setShowModal] = useState(false); // PV loader message
  const [isNowLive, setIsNowLive] = useState(false);
  const [currentSec, setCurrentSec] = useState(0);
  const [currentPeriod, setCurrentPeriod] = useState(0);
  const [playersTrails, setPlayersTrails] = useState<PlayersTrails>({});
  const [liveCurrentSec, setLiveCurrentSec] = useState(0);

  // main useEffect, requests tracks for the first time.
  useEffect(() => {
    const { startTime, datasetRefId } = practiceData;
    if (startTime && (datasetRefId || practiceData.gameId)) {
      const startTime = getPracticeViewerStartTime(practiceData, currentSec, setOnPlay, setCurrentSec, liveCurrentSec);
      getPracticeTimeRange(startTime, DEFAULT_LOADING_SECS);
    }
  }, [practiceData.startTime, practiceData.datasetRefId, practiceData.gameId]);

  useEffect(() => {
    if (hasBlinks && !isFetching && !isNowLive) {
      handleProgressiveTracksFetching(practiceData.startTime);
    }
  }, [isFetching, currentSec, liveCurrentSec, hasBlinks]);

  useEffect(() => {
    //LIVE TRACKS CALLS
    if (hasBlinks && isNowLive) {
      handleLiveTracksFetching(practiceData.startTime);
    }
  }, [isFetching, liveCurrentSec]);

  useEffect(() => {
    const { fieldIndex } = field;
    if ((fieldIndex || fieldIndex === 0) && fields[fieldIndex] && fields[fieldIndex].image) {
      setField(fields[fieldIndex]);
      if (wasOnPlay || practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME) setOnPlay(true);
    } else if (fields.length) {
      setWasOnPlay(onPlay);
      setOnPlay(false);
      getSpecificFieldImage(practiceData.homeTeam.teamId, practiceId, fieldIndex!);
    }
  }, [fields]);

  // Failed requests handler.
  useEffect(() => {
    if (fetchTries > 0 && !blinks) {
      const loadingSecs = practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME  ? LIVE_DEFAULT_LOADING_SECS : DEFAULT_LOADING_SECS;
      fetchTimeout = setTimeout(() => {
        const startTime = getPracticeViewerStartTime(practiceData, currentSec, setOnPlay, setCurrentSec, liveCurrentSec);
        getPracticeTimeRange(startTime, loadingSecs);
      }, loadingSecs * 1000);
    }
  }, [fetchTries]);

  useEffect(() => {
    if (metricSec || metricSec === 0) {
      onClickEventsDispatcher(metricSec / 10);
      setMetricCurrentSecond(null, practiceId);
    }
  }, [metricSec]);

  // delete trail from state when unselected from table rows
  useEffect(() => {
    Object.keys(playersTrails).forEach(key => {
      if (!pinnedRows.includes(key)) {
        const unmutedTrails = { ...playersTrails };
        delete unmutedTrails[key];
        setPlayersTrails(unmutedTrails);
      }
    });
  }, [pinnedRows]);

  // PV Loader Modal useEffect
  // useEffect(() => {
  //   const actualSec = currentSec > 0 ? removeDecimalsFromSecs(currentSec) : currentSec;
  //   const lastBlink = blinks ? parseInt(Object.keys(blinks)[Object.keys(blinks).length - 1]) : 0;
  //   if (!hasBlinks || !blinks || (lastBlink - actualSec < 0)) {
  //     !showModal && setShowModal(true);
  //   } else if (showModal) {
  //     setShowModal(false);
  //   }
  // }, [currentSec, blinks, hasBlinks]);

  // Method to check if the below useInterval should run.
  const handleSecondsPassedBy = () => {
    const { startTime, endTime, type } = practiceData;
    const actualSec = currentSec > 0 ? removeDecimalsFromSecs(currentSec) : currentSec;
    const lastBlink = blinks ? parseInt(Object.keys(blinks)[Object.keys(blinks).length - 1]) : 0;
    const lastPracticeSecond = (parseInt(((endTime - startTime) / 1000).toFixed(0)) - 1) * 10; // 10 for the decimals

    if (
      (currentSec < lastPracticeSecond || type === LIVE_PRACTICE || type === PAST_GAME) &&
     //(lastBlink - actualSec >= 0) && // if there are blinks ahead the current sec
      !metricSec // if the metricSec flag doesn't exist to avoid multiple setCurrentSec
    ) {
      checkOnProgressivePeriodChange(currentSec, practiceData, currentPeriod, setCurrentPeriod);
      return true;
    } else if (currentSec >= lastPracticeSecond && (type !== LIVE_PRACTICE && type !== PAST_GAME)) {
      setOnPlay(false);
    }
    return false;
  };

  // Current Time Handler
  useInterval(() => {
    if (!isNowLive) {
      setCurrentSec(currentSec + 1);
    }
  }, hasBlinks && onPlay && handleSecondsPassedBy() ? 100 : null);

  // Live running seconds handler
  useInterval(() => {
    // get actual real life seconds
    const { actualSeconds } = getRealTimeActualSeconds(practiceData.startTime);
    setLiveCurrentSec(actualSeconds);
    if (isNowLive && onPlay && !metricSec) {
      // if the practice is currently live the currentSec is the real time seconds
      setCurrentSec(actualSeconds);
      checkOnProgressivePeriodChange(currentSec, practiceData, currentPeriod, setCurrentPeriod);
    }
  }, practiceData && practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME ? 100 : null);

  /**
   * The Master Method to dispatch every request for Tracks.
   * @param {timestamp} timeStart start time of the request.
   * @param {number} timeRange requested seconds.
   */
  const getPracticeTimeRange = (timeStart: number, timeRange: number) => {
    const timeEnd = timeStart + (1000 * timeRange); // convert seconds to ms + startTime
    getPracticePlayback(timeStart, timeEnd, practiceId, practiceData.type === PAST_GAME );

    if (practiceData.type === LIVE_PRACTICE || practiceData.type === PAST_GAME ) {
      livePracticePlayers(timeStart); // each time a request is made call players for live
      if (pinnedRows.length && currentTab === METRICS_CATEGORIE) {
        getPlayersMetrics(pinnedRows, practiceId); // metrics req on metrics tab only
      }
    }
  };

  /**
   * Fetch for Post or currently "No Live" practices
   * @param {timestamp} startTime
   */
  const handleProgressiveTracksFetching = (startTime : number) => {
    const actualSec = currentSec > 0 ? removeDecimalsFromSecs(currentSec) : currentSec;
    // 90 secs cause' the tracks memory limit is 120s, so it can store 90s forwards and 30s backwards
    // reqSecs is the last tracks requested second.
    if ((reqSecs - actualSec) < 90) {
      const timeStart = startTime + (1000 * reqSecs); // reqSecs to ms
      const actualLiveSec = liveCurrentSec > 0 ? removeDecimalsFromSecs(liveCurrentSec) : liveCurrentSec;
      // if practice is post or if its live it has more conditions
      if (
        practiceData.type === PAST_PRACTICE || (
        reqSecs <= actualLiveSec && 
        isNotCurrentlyLive(currentSec, liveCurrentSec)) // 15s error margin for progressive requests
      ) {
        getPracticeTimeRange(timeStart, DEFAULT_LOADING_SECS);
      }
    } 
  };

  const handleLiveTracksFetching = (startTime: number) => {
    const { actualTime, actualSeconds } = getRealTimeActualSeconds(startTime);
    const diffRealTime = reqSecs - removeDecimalsFromSecs(actualSeconds);
    // request each requestSeconds/2 - currently each 5 seconds for live
    if (!isFetching && diffRealTime <= (LIVE_DEFAULT_LOADING_SECS / 2)) {
      getPracticeTimeRange(actualTime, LIVE_DEFAULT_LOADING_SECS);
    } 
  };

  const getCurrentTimePeriod = (startTime: number, drills: Array<Drill>, type: string) => {
    if (drills && drills.length) {
      const arrayDrills = type === LIVE_PRACTICE ? drills.filter(drill => drill.startTs) : drills;
      arrayDrills.some((period, index) => {
        const actualTime = startTime + (removeDecimalsFromSecs(currentSec) * 1000);
        if (period.startTs <= actualTime && (actualTime < period.endTs || !period.endTs)) {
          if (currentPeriod !== index) setCurrentPeriod(index);
          return true;
        }
        return false;
      });
    }
  };

  /**
   * Handler for every "onClick" event that needs a request.
   * @param {number} actualSeconds event timestamp in seconds 
   * @param {number=} periodIndex event period index
   */
  const onClickEventsDispatcher = (actualSeconds : number, periodIndex?: number) => {
    const { startTime: practiceBegin, drills, type } = practiceData;
    const startTime = practiceBegin + (actualSeconds * 1000); 
    cleanPendingRequests();
    setPlayersTrails({});
    if (isNowLive) setIsNowLive(false);
    setCurrentSec(actualSeconds * 10);
    getPracticeTimeRange(startTime, DEFAULT_LOADING_SECS);

    if (periodIndex || periodIndex === 0) {
      setCurrentPeriod(periodIndex);
    } else {
      getCurrentTimePeriod(practiceBegin, drills, type);
    }
  };

  const cleanPendingRequests = () => {
    clearPlayersTracksData(practiceId); // cleans all previous tracks
    cleanControllerSet(CONTROLLER_SET); // cleans tracks requests

    if (fetchTimeout) {
      clearTimeout(fetchTimeout);
      fetchTimeout = undefined;
    }
  };

  const practiceMapProps = {
    show,
    field,
    mapImage,
    setField,
    setOnShow,
    dimensions,
    currentSec,
    currentPeriod,
    playersTrails,
    setPlayersTrails
  };

  return (
    <>
      <div className="ol-map-section">
        <div className="title-m">
          {field && field.type ? field.type : 'Default'} Field
          {practiceData.type !== PAST_GAME && hasBlinks &&
            <div className="current-period">
              <span className="period-label">Current Period:</span>
              {' ' + practiceData.drills[currentPeriod].drillType}
            </div>
          }
        </div>

        {shouldMapMount && <PracticeMap {...practiceMapProps} />}

        {/* {showModal && <LoadingDialog errorMsg={errorMsg} />} */}
      </div>

      <div className="footer-map">
        <div className="map-timeline">
          <Timeline
            setOnPlay={setOnPlay}
            isNowLive={isNowLive}
            currentSec={currentSec}
            setIsNowLive={setIsNowLive}
            setCurrentSec={setCurrentSec}
            playersTrails={playersTrails}
            liveCurrentSec={liveCurrentSec}
            setPlayersTrails={setPlayersTrails}
            currentPeriodIndex={currentPeriod}
            getCurrentTimePeriod={getCurrentTimePeriod}
            onClickEventsDispatcher={onClickEventsDispatcher}
          />
        </div>
        <div className="line-btn">
          <ActionButtons 
            onPlay={onPlay}
            setOnPlay={setOnPlay}
            isNowLive={isNowLive}
            currentSec={currentSec}
            practiceData={practiceData}
            currentPeriodIndex={currentPeriod}
            onClickEventsDispatcher={onClickEventsDispatcher}
          />
        </div>
      </div>
    </>
  );
};

export default MapViewer;